import React, { useEffect, useRef } from 'react'
import { Transition, TransitionStatus } from 'react-transition-group'

import UIIcon, { UIIconRef } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'

type DecorationParticleProps = React.HTMLProps<HTMLDivElement> & {
  xStart?: number
  xEnd?: number
  noAnimation?: boolean
}
function DecorationParticle({ className, xStart = 0, xEnd = 100, noAnimation }: DecorationParticleProps) {
  const particle = useRef<HTMLDivElement>(null)
  const icon = useRef<UIIconRef>(null)

  const { gsap } = useGSAP()

  useEffect(() => {
    if (noAnimation) {
      if (icon.current) {
        gsap.set(icon.current, { x: xEnd, rotate: -45 })
      }
      return
    }
    if (!icon.current) return
    // TODO Recreate the animation on screen resize
    gsap.to(icon.current, {
      duration: 0.4,
      ease: 'power2.out',
      delay: 0.3,
      x: xStart,
    })
    gsap.to(icon.current, {
      rotate: -45,
      duration: 1.5,
      ease: 'linear',
      x: xEnd,
    })
    const timeline = gsap.timeline({ repeat: 0 })
    return () => {
      timeline?.kill()
    }
  }, [particle, gsap, noAnimation, xStart, xEnd])

  return (
    <div ref={particle} className={`absolute ${className}`}>
      <UIIcon ref={icon} className='text-384 text-secondary' icon='background3' />
    </div>
  )
}

export type DecorationProps = React.HTMLProps<HTMLDivElement> & {
  show: boolean
  noAnimation?: boolean
}

export default function Decoration({ className, show, noAnimation }: DecorationProps) {
  const elementRef = useRef<HTMLDivElement>(null)
  const getTransitionStyle = (state: TransitionStatus) => {
    switch (state) {
      case 'entering':
        return 'opacity-0'
      case 'entered':
        return 'opacity-100'
      case 'exiting':
        return 'opacity-0'
      default:
        return 'opacity-0'
    }
  }

  return (
    <Transition
      in={show}
      appear
      mountOnEnter
      unmountOnExit
      nodeRef={elementRef}
      timeout={{
        enter: 0,
        exit: 2,
      }}
    >
      {(state) =>
        state !== 'exited' && (
          <div
            ref={elementRef}
            aria-hidden='true'
            className={`Decoration absolute top-0 left-0 w-full h-full overflow-hidden z-1 pointer-events-none transition-opacity duration-500 ease-out-quad ${className} ${getTransitionStyle(state)}`}
          >
            <DecorationParticle className='-translate-y-1/3' noAnimation={noAnimation} />
            <DecorationParticle
              className='bottom-0 right-0 translate-y-1/3'
              xStart={0}
              xEnd={-100}
              noAnimation={noAnimation}
            />
          </div>
        )
      }
    </Transition>
  )
}
